import angular from 'angular'

import StrictObj from './strict-obj'
import callTracker from './call-tracker'

import { safeSet, callWith } from './bits'
import { has, pick, keys, forEach, toArray, constant, findIndex, flattenDeep, isPlainObject } from 'lodash-es'

angular
  .module('ggs.utils')
  .factory('LazyResource', LazyResourceFactory)
  .factory('LazyResources', LazyResourcesFactory)

/*
  Usage:
    let lazy = LazyResource('foo', 'bar')

    console.log('lazy.foo - called')
    lazy.foo([1,2,3]).then(function(result) {
      console.log('lazy.foo - result:', result)
    })

    console.log('lazy.foo - allowed')
    lazy.foo.allow(function(array) {
      return array.reverse()
    })

    // > lazy.foo - called
    // > lazy.foo - allowed
    // > lazy.foo - result: [3, 2, 1]
*/

const SYNC = 'sync'
const CONST = 'const'
const ASYNC = 'async'

function LazyResourceFactory($q, qh) {
  return function LazyResource(...args) {
    const lazy = StrictObj({})
    const wait = StrictObj({})
    const real = {}
    const sync = {}

    if (isPlainObject(args[0])) {
      const defObj = args[0]
      if (has(defObj, SYNC)) defineSync(defObj[SYNC])
      if (has(defObj, ASYNC)) defineAsync(defObj[ASYNC])
      if (has(defObj, CONST)) defineConst(defObj[CONST])
    } else {
      defineAsync(args)
    }

    lazy[SYNC] = defineSync
    lazy[ASYNC] = defineAsync
    lazy[CONST] = defineConst
    return lazy

    function defineSync(...args) {
      const methods = flattenDeep(args)
      methods.forEach(appendSync)
      return lazy
    }

    function defineAsync(...args) {
      const methods = flattenDeep(args)
      methods.forEach(appendAsync)
      return lazy
    }

    function defineConst(name, val) {
      if (isPlainObject(name)) {
        forEach(name, (val, key) => defineConst(key, val))
      } else {
        appendConst(name, val)
      }

      return lazy
    }

    function appendConst(name, val) {
      lazy[name] = val
    }

    function appendSync(name) {
      sync[name] = null
      lazy[name] = callTracker((...args) => {
        if (!sync[name]) {
          throw new Error(`LazyResource::sync[name=${name}]: method should be allowed first!`)
        }
        return sync[name](...args)
      })

      lazy[name]._name = name
      lazy[name]._func = null

      lazy[name].allow = function allow(func) {
        sync[name] = func
        lazy[name]._func = func
        return lazy
      }

      lazy[name].flush = function flush() {
        sync[name] = null
        lazy[name]._func = null
        return lazy
      }

      lazy[name].isReady = function isReady() {
        return !!sync[name]
      }

      lazy[name].replace = function replace(func) {
        if (lazy[name].isReady()) {
          lazy[name].flush()
        }
        lazy[name].allow(func)
        return lazy
      }
    }

    function appendAsync(name) {
      wait[name] = []
      real[name] = null
      lazy[name] = callTracker(function lazyMethodWrapper() {
        const args = arguments
        if (real[name]) return qh.try(real[name], args)
        const call = () => qh.try(real[name], args)
        return $q((onReady) => wait[name].push(onReady)).then(call)
      })

      lazy[name]._name = name
      lazy[name]._func = null

      lazy[name].allow = function allow(func) {
        const local = wait[name].concat()
        wait[name] = null
        real[name] = func
        lazy[name]._func = func
        local.forEach((onReady) => onReady())
        return lazy
      }

      lazy[name].flush = function flush() {
        wait[name] = []
        real[name] = null
        lazy[name]._func = null
        return lazy
      }

      lazy[name].isReady = function isReady() {
        return !!real[name]
      }

      lazy[name].replace = function replace(func) {
        if (lazy[name].isReady()) {
          lazy[name].flush()
        }
        lazy[name].allow(func)
        return lazy
      }
    }
  }
}

function LazyResourcesFactory($q, qh) {
  return function LazyResources(...methods) {
    const resources = StrictObj([])

    methods.forEach(attach)
    resources.add = add
    resources.del = del
    resources.has = has
    return resources

    function add(...args) {
      const resource = Resource(...args)
      const dupIndex = findIndex(resources, resource)
      if (dupIndex === -1) {
        resources.push(resource)
      }

      return resources
    }

    function del(...args) {
      const resource = Resource(...args)
      const dupIndex = findIndex(resources, resource)
      if (dupIndex !== -1) {
        resources.splice(dupIndex, 1)
      }

      return resources
    }

    function has(...args) {
      const resource = Resource(...args)
      const dupIndex = findIndex(resources, resource)
      return dupIndex !== -1
    }

    function attach(method) {
      resources[method] = StrictObj({
        someSeries: (...args) => {
          const functions = resources.map((resource) => resource[method])
          const promisified = functions.map(qh.wrap)
          return iterate()

          function iterate(index = 0) {
            const func = promisified[index]
            if (!func) return $q.resolve(false)
            return func(...args).then((resp) => {
              if (!!resp) return $q.resolve(true)
              return iterate(++index)
            })
          }
        },
        everySeries: (...args) => {
          const functions = resources.map((resource) => resource[method])
          const promisified = qh.makeAnd(...functions)
          return iterate()

          function iterate(index = 0) {
            const func = promisified[index]
            if (!func) return $q.resolve(true)
            return func(...args).then((resp) => {
              if (!resp) return $q.resolve(false)
              return iterate(++index)
            })
          }
        },
        mapParallel: (...args) => {
          const functions = resources.map((resource) => resource[method])
          const promisified = functions.map(qh.wrap)
          return $q.all(promisified.map(callWith(...args)))
        },
        eachParallel: (...args) => {
          return resources[method].mapParallel(...args).then(constant(undefined))
        }
      })
    }

    function Resource(input) {
      const args = toArray(arguments)
      const like = isPlainObject(input) && methods.length === keys(pick(input, methods)).length && arguments.length === 1

      let resource = null
      if (like) {
        resource = StrictObj(pick(input, methods))
      } else {
        resource = methods.reduce((resource, method, index) => safeSet(resource, method, args[index]), StrictObj({}))
      }

      return resource
    }
  }
}
